export const overviewDocumentation = `
## About This Documentation

This internal documentation system provides comprehensive information about the Verdin Labs application for development team members, testers, and backend developers. The documentation is organized by application sections and functionality to make it easy to find specific information.

## How to Use This Documentation

### Navigation
- **Left Sidebar**: Browse through the hierarchical structure of documentation topics
- **Search**: Use the search field in the header to quickly find specific topics
- **Breadcrumbs**: Track your current location within the documentation structure

### Documentation Structure
Each section follows a consistent format prioritized for testing and development needs:

1. **Fields Specification** - Detailed tables of all form fields, their types, validation rules, and requirements
2. **Validation Rules** - Complete validation logic and error conditions
3. **API Specifications** - Backend endpoints, request/response formats, and status codes
4. **Permissions** - Access control requirements and user role dependencies
5. **UI Functionality** - User interface behavior, actions, and interactions

### Available Sections

#### ✅ Completed Documentation
- **Patients > Patient Edit/Create** - Complete patient management documentation
- **Patients > Patient List** - Patient listing, filtering, and table functionality

#### 🚧 In Development
- Dashboard functionality
- Account management
- Orders and assessments
- Claims and reporting
- User management
- All other application sections

*Sections marked as disabled (grayed out) in the navigation are currently being developed.*

## Application Overview

### Core Functionality
The QHS application provides healthcare management tools including:

- **Patient Management** - Create, edit, and manage patient records with comprehensive health information
- **Account Management** - Multi-tenant account system with Distribution Sponsor capabilities
- **Order Processing** - Healthcare order creation and tracking
- **Assessment Tools** - Patient assessment and survey functionality
- **Claims Reporting** - Insurance claims processing and reporting
- **Resource Management** - Healthcare resource and document management
- **User Administration** - Role-based access control and user management

### Account Types

#### Distribution Sponsor (DS) Accounts
- Can access multiple child accounts
- Additional permissions with \`ACCOUNTS_GET\`
- Extended table views with account-specific columns
- Multi-account patient and data management
- Enhanced reporting capabilities across accounts

#### Regular Accounts
- Single account access
- Standard patient and resource management
- Account-specific data and reporting
- Standard permission set

### Permission System
The application uses a comprehensive permission-based access control system:

- **Resource Permissions** - Control access to specific data types (patients, orders, etc.)
- **Action Permissions** - Control specific operations (create, read, update, delete)
- **Account-Level Permissions** - Control cross-account functionality for DS accounts
- **UI Element Control** - Permissions determine visible columns, buttons, and features

### Key Technologies
- **Frontend**: React with Material-UI components
- **Backend**: C# .NET with FluentValidation
- **Database**: Entity Framework with SQL Server
- **Authentication**: Role-based access control
- **API**: RESTful endpoints with JSON responses

## Documentation Standards

This documentation follows established standards to ensure consistency and completeness:

### Field Documentation Format
All form fields are documented with:
- **Required/Optional status** - ✓ for required, blank for optional
- **Editable status** - ✓ for editable, ✗ for read-only
- **Data types** - String, Number, Boolean, Enum, Object, etc.
- **Formatting rules** - Phone numbers, emails, dates, etc.
- **Validation constraints** - Length limits, format requirements, etc.
- **Descriptions** - Clear explanations including business context

### API Documentation Format
All endpoints include:
- **HTTP methods and URLs** - Complete endpoint specifications
- **Request/Response examples** - Real JSON examples with all fields
- **Status codes** - All possible response codes with scenarios
- **Error formats** - Validation and system error response structures
- **Query parameters** - Filtering, pagination, and feature flags

### Permission Documentation
All features document:
- **Required permissions** - Exact permission names needed
- **Permission context** - Current account vs child account permissions
- **Conditional access** - How permissions affect UI and functionality
- **Error handling** - What happens when permissions are insufficient

## Getting Help

### For Testers
- Use field specification tables to understand form requirements
- Reference validation rules for testing edge cases
- Check permission requirements for access testing
- Use API documentation for integration testing

### For Backend Developers
- Reference API specifications for endpoint implementation
- Use permission documentation for access control implementation
- Follow validation rules for backend validation logic
- Use field specifications for database schema requirements

### For Frontend Developers
- Reference UI functionality sections for component behavior
- Use permission documentation for conditional rendering
- Follow field specifications for form implementation
- Use API documentation for service layer development

## Development Resources

### Design and Assets
- **[UI/UX Design (Sketch)](https://www.sketch.com/s/e242a7e9-d72b-48f6-a3b6-f11a93bb68e6/a/AZJlk7z)** - Complete design system and mockups for the Verdin Labs application

### Code Repositories
- **[QHS Dashboard Repository](https://dev.azure.com/medical-practice-income/qhs/_git/dashboard)** - Main frontend application repository on Azure DevOps

### Application Environments
- **[Development Environment](https://my.dev.qhslab.com/login)** - Testing and development deployment
- **[Production Environment](https://my.qhslab.com/login)** - Live production application

### Project Management
- **[JIRA Project Board](https://qhslab.atlassian.net/jira/software/projects/QHS/boards/2)** - Sprint planning, task tracking, and project management

### Localization
- **[Localizely Translation Platform](https://app.localizely.com/login)** - Multi-language translation management for the application

### Documentation and Specifications
- **[HTML Template Documentation](https://docs.google.com/document/d/1XMuFCNqgw0GpU0hMdx3seLiogaEOOdoYq5cKNiNsMm4/edit?pli=1#heading=h.2z8gew1cmyhe)** - HTML template formatting guide for backend file generation
- **[Markdown Syntax Documentation](https://docs.google.com/document/d/1kYuxfin-6hB2AzWXykI-LQeWEMNIdDYPVd_wMv_CmnQ/edit?pli=1#heading=h.k8s71ps95esa)** - Markdown syntax guide for dashboard report rendering

### Technical References
- **[Material-UI Transitions](https://mui.com/material-ui/customization/transitions/)** - UI animation and transition documentation for React components

### Monitoring and Logging
- **[Sematext Logs](https://apps.sematext.com/ui/logs/73141/explore?startDate=1732542907917&endDate=1732548307917)** - Application logging and monitoring dashboard

### Contact Information
For questions about this documentation or requests for additional sections:
- Contact the QHS development team
- Create issues in the project repository
- Reach out to the technical documentation maintainers

---

*This documentation system is continuously updated as new features are developed and existing functionality is enhanced. Check back regularly for updates to existing sections and new documentation areas.*
`;
